// P1-3 follow-up: what values does THIS entry carry?
//
// Extracted after Christian's 08/10/2026 staging test: "sometimes it keeps the selected asset
// type after scanning, and other times it clears it."
//
// ── WHY THE ASSET TYPE WAS INCONSISTENT ─────────────────────────────────────
// A gun scan used to set a `dataFilter`, which ran the grid-filter effect, which called
// setAssetEditorFields() and so wrote THE MATCHED ROW'S asset_type_id over whatever the
// operator had chosen in the dropdown. When P1-3 made the gun call the scan entry point
// directly, that side effect disappeared from the gun path but stayed on the typed path (the
// Serial # field still sets a filter on every keystroke). So the dropdown survived a scan or was
// overwritten-then-reset depending only on where focus happened to be -- and since a refusal
// focuses the Serial # field, it changed in runs.
//
// The fix is to stop the grid filter touching editor state at all and decide an entry's values
// here, once, explicitly. This module is the rule; it has no React in it.
//
// ── THE RULE ────────────────────────────────────────────────────────────────
// Three states, and they need two separate answers -- conflating them is a mistake I made twice
// while writing this, so it is spelled out:
//
//   * BOUND TO THIS ROW (`form.assetId === assetId`) -- the operator is looking at this row's
//     own fields, so every change is deliberate, INCLUDING one they emptied. Their Asset Type
//     overrides the row's stored type. This is "select a listed item, correct its Asset Type,
//     press Collect"; taking the stored values instead would discard the correction.
//   * UNBOUND (`form.assetId == null`) -- nothing was loaded from a row, so anything in the form
//     is the operator's own typing (or, straight after an accepted entry, just the pinned
//     values). Use it where it is non-empty, so a typed Asset Tag or a pinned note lands -- but
//     an EMPTY field means "I didn't say", not "clear it", so a scan of a listed item must not
//     blank that row's stored description. And the row's own Asset Type wins, because the
//     dropdown is holding the previous item's pinned type.
//   * BOUND TO ANOTHER ROW -- the form's contents belong to that other item (someone clicked a
//     grid row, then scanned something else), so only the deliberately pinned values carry over.
//     This is what stops row A's notes being written onto row B.

/**
 * Decide the values an entry should carry.
 *
 *   assetId - the row this entry resolved to (null for a new/unexpected item)
 *   form    - { assetId, assetTypeId, assetTag, description, notes } as currently displayed
 *   pins    - { pinAssetType, pinnedAssetType, pinDescription, pinNotes }
 *
 * Returns { assetTypeId, assetTag, description, notes, fromEditor }.
 */
const entryValuesFor = ({ assetId = null, form = {}, pins = {} } = {}) => {
    const {
        pinAssetType = false,
        pinnedAssetType = '',
        pinDescription = false,
        pinNotes = false
    } = pins;

    const boundToThisRow = assetId != null
        && form.assetId != null
        && String(form.assetId) === String(assetId);
    const boundToAnotherRow = form.assetId != null && !boundToThisRow;

    // Two independent flags. Both default to false, and only the bound-to-this-row case sets
    // them, which is what keeps a scan from overwriting a listed row's stored data:
    //
    //   fromEditor       - is an EMPTY field a deliberate clear?
    //   typeOverridesRow - may the form's Asset Type override a matched row's stored type?
    const values = boundToAnotherRow
        ? {
            // The pinned type is the operator's standing choice (P1-15). It is never written by a
            // matched row, so it cannot drift to whatever was last scanned.
            assetTypeId: pinAssetType ? (pinnedAssetType || null) : null,
            // An asset tag is per-item and is never pinned, so it must not leak to the next
            // entry. This is also what keeps a stale tag out of the scan classification.
            assetTag: '',
            description: pinDescription ? (form.description ?? '') : '',
            notes: pinNotes ? (form.notes ?? '') : ''
        }
        : {
            assetTypeId: form.assetTypeId ?? null,
            assetTag: form.assetTag ?? '',
            description: form.description ?? '',
            notes: form.notes ?? ''
        };

    return { ...values, fromEditor: boundToThisRow, typeOverridesRow: boundToThisRow };
};

/**
 * Merge an entry's values over a matched row to build the update payload's editable fields.
 *
 * Tag / description / notes: when the values came from the editor they win outright (an emptied
 * field is a deliberate clear); otherwise they apply only where non-empty, so a pinned Notes
 * value still lands but an untouched field keeps the intent list's own answer.
 *
 * Asset type: the row's own stored type wins UNLESS the operator is looking at that row
 * (`typeOverridesRow`). See the note in entryValuesFor.
 */
const applyEntryValues = (asset = {}, values = {}) => {
    const pick = (entered, stored) => (values.fromEditor
        ? (entered ?? '')
        : (entered != null && entered !== '' ? entered : stored));

    return {
        asset_type_id: values.typeOverridesRow
            ? (values.assetTypeId || asset.asset_type_id)
            : (asset.asset_type_id ?? values.assetTypeId ?? null),
        asset_tag: pick(values.assetTag, asset.asset_tag),
        description: pick(values.description, asset.description),
        collection_notes: pick(values.notes, asset.collection_notes)
    };
};

const exports = { entryValuesFor, applyEntryValues };

export default exports;
export { entryValuesFor, applyEntryValues };
